<script lang="ts">
  /**
   * Advisory framing + the freshness stamp, above the fold and outside the map surface.
   *
   * The stamp is here rather than only in the map's filter panel because of who reads it and when.
   * On a phone that panel starts collapsed, so the one place a visitor could learn how old the
   * airspace data is required opening a panel they have no reason to open — while `kullanim.html`
   * promised the age was written beside every layer. Data age is the fact that decides whether to
   * trust anything else on the page; it belongs where the advisory sentence is.
   */
  import type { PlanningDataStatus } from '$dp/lib/planning/status.ts';

  let { status = null }: { status?: PlanningDataStatus | null } = $props();

  // Four states, each with its own sentence. "Unknown" is deliberately styled as a warning rather
  // than left blank: an absent age reads as "no news is good news", which is the wrong default for
  // data whose whole value is being current.
  const fresh = $derived.by(() => {
    if (!status || status.loading) return { text: 'Veri yükleniyor…', tone: 'wait' as const };
    if (status.failed) return { text: '⚠ Bölge verisi alınamadı', tone: 'err' as const };
    if (status.dataAt == null) return { text: '⚠ Veri yaşı bilinmiyor', tone: 'err' as const };
    if (!status.notamsOk) return { text: `⚠ Veri ${status.ageLabel} — geçici kısıtlamalar (NOTAM) gösterilmiyor`, tone: 'warn' as const };
    return { text: `Veri: ${status.ageLabel}`, tone: 'ok' as const };
  });
</script>

<header class="bar">
  <div class="brand"><strong>havasaha</strong><span class="tag">drone uçuş bölgeleri</span></div>
  <p class="advisory">
    <span class="badge">Bilgilendirme amaçlıdır</span>
    Yasal dayanak SHGM'dir; uçuştan önce
    <a href="https://ihatakip.shgm.gov.tr/" target="_blank" rel="noopener">İHATTYS</a> üzerinden doğrulayın.
  </p>
  <!-- aria-live so a screen reader hears the layer being withdrawn, which is the one change here
       that alters what the map is safe to be used for. -->
  <p class="fresh {fresh.tone}" role="status" aria-live="polite">{fresh.text}</p>
  <nav class="legal">
    <a href="https://dronosfer.tr" target="_blank" rel="noopener">Dronosfer</a><a href="https://mdemirbas.com" target="_blank" rel="noopener">mdemirbas.com</a><a href="/kvkk">KVKK</a><a href="/cerez">Çerez</a><a href="/kullanim">Kullanım</a>
  </nav>
</header>

<style>
  .bar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px 16px;
    padding: 8px 14px;
    background: var(--surface-solid);
    border-bottom: 1px solid var(--line);
    font: 13.5px/1.4 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    color: var(--text);
    /* The bar is above the map's own overlays, so nothing inside the map can cover the advisory
       sentence. It no longer overlaps anything, because it is a sibling in a column, not a layer. */
    z-index: 30;
  }
  .brand { display: flex; align-items: baseline; gap: 8px; white-space: nowrap; font-size: 16px; }
  .tag { color: var(--text-soft); font-size: 13px; }
  .advisory { margin: 0; color: var(--text-soft); flex: 1; min-width: 0; }
  .badge {
    display: inline-block; margin-right: 8px; padding: 2px 8px; border-radius: 999px;
    background: color-mix(in srgb, var(--warn) 20%, transparent); color: var(--warn); font-weight: 600; font-size: 12.5px;
  }
  .fresh { margin: 0; white-space: nowrap; font-size: 12.5px; color: var(--text-soft); }
  .fresh.warn { color: var(--warn); font-weight: 600; white-space: normal; }
  .fresh.err { color: var(--bad); font-weight: 600; }
  .legal { display: flex; gap: 12px; font-size: 12.5px; color: var(--text-soft); }
  a { color: inherit; }

  @media (max-width: 860px) {
    .bar { flex-wrap: wrap; gap: 6px 14px; }
    /* Order: brand, freshness, advisory, legal. The age moves ABOVE the advisory sentence on a
       phone because it is the shorter and the more perishable of the two. */
    .advisory { flex-basis: 100%; order: 3; }
    .fresh { order: 2; white-space: normal; }
    .legal { order: 4; }
  }
</style>
